// Story 1.28 — admin portfolio-application detail. Wrapped by
// <AdminGuard> in App.tsx.
//
// Two-column layout: creator+applicant info on the left, portfolio
// context + effective hello-text on the right. Action bar at the
// bottom carries Approve + Reject (Reject opens a modal with an
// optional reason textarea; reason min 5 chars when provided).

import { useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Textarea,
} from "@greenhold/components";

import { CreatorPreviewShare } from "../features/admin/portfolios/CreatorPreviewShare";
import {
  type AdminPortfolioApplicationDetail,
  useAdminPortfolioApplicationDetail,
} from "../features/admin/useAdminPortfolioApplicationDetail";
import {
  useApprovePortfolioApplicationMutation,
  useRejectPortfolioApplicationMutation,
} from "../features/admin/useAdminPortfolioApplicationMutations";

const REJECT_REASON_MIN = 5;
const REJECT_REASON_MAX = 1000;

function statusBadgeVariant(
  status: "pending" | "approved" | "rejected" | "withdrawn",
): "warning" | "success" | "destructive" | "secondary" {
  switch (status) {
    case "pending":
      return "warning";
    case "approved":
      return "success";
    case "rejected":
      return "destructive";
    case "withdrawn":
      return "secondary";
  }
}

export function AdminPortfolioApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useAdminPortfolioApplicationDetail(id ?? "");

  if (!id) {
    return (
      <main className="min-h-screen bg-brand-paper-200 p-6">
        <p className="text-destructive">Missing application id in URL.</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <Link
          to="/admin/portfolio-applications"
          className="text-sm underline decoration-dotted opacity-80"
        >
          ← Back to queue
        </Link>
        {query.isPending && <p className="opacity-70">Loading…</p>}
        {query.isError && (
          <Card>
            <CardContent className="p-6 space-y-3">
              <p className="text-destructive">
                Couldn&apos;t load this application. {query.error.message}
              </p>
              <Button variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        )}
        {query.data && (
          <DetailBody id={id} detail={query.data.data} />
        )}
      </div>
    </main>
  );
}

function DetailBody({
  id,
  detail,
}: {
  id: string;
  detail: AdminPortfolioApplicationDetail;
}) {
  const navigate = useNavigate();
  const approve = useApprovePortfolioApplicationMutation(id);
  const reject = useRejectPortfolioApplicationMutation(id);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const isPending = detail.status === "pending";

  async function handleApprove() {
    try {
      await approve.mutateAsync();
      toast.success(
        `Approved ${detail.creator_display_name ?? "creator"} for ${detail.portfolio_title}.`,
      );
      navigate("/admin/portfolio-applications");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Approve failed.";
      toast.error(msg);
    }
  }

  async function handleReject() {
    const trimmed = reason.trim();
    // The validator on the backend treats empty/whitespace as omission;
    // we forward null when blank so the API doesn't 400 on "".
    const payload = trimmed.length === 0 ? null : trimmed;
    try {
      await reject.mutateAsync(payload);
      toast.success(`Rejected ${detail.creator_display_name ?? "creator"}.`);
      setRejectOpen(false);
      navigate("/admin/portfolio-applications");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Reject failed.";
      toast.error(msg);
    }
  }

  const reasonLen = reason.trim().length;
  const reasonInvalid = reasonLen > 0 && reasonLen < REJECT_REASON_MIN;
  const reasonTooLong = reasonLen > REJECT_REASON_MAX;
  const submitDisabled = reject.isPending || reasonInvalid || reasonTooLong;

  return (
    <>
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">
            {detail.creator_display_name ?? "(no display name)"}
            {detail.creator_handle && (
              <span className="opacity-60 ml-3 font-mono text-base">
                @{detail.creator_handle}
              </span>
            )}
          </h1>
          <p className="opacity-70 text-sm mt-1">
            Application for{" "}
            <Link
              to={`/admin/portfolio-applications?portfolio_id=${detail.portfolio_id}`}
              className="underline decoration-dotted"
            >
              {detail.portfolio_title}
            </Link>
          </p>
        </div>
        <Badge variant={statusBadgeVariant(detail.status)}>
          {detail.status}
        </Badge>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left — creator + applicant */}
        <Card>
          <CardHeader>
            <CardTitle>Creator</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>
              <span className="opacity-60">Email:</span>{" "}
              {detail.applicant_email}{" "}
              {detail.applicant_email_verified && (
                <Badge variant="secondary">verified</Badge>
              )}
            </p>
            <p>
              <span className="opacity-60">Account created:</span>{" "}
              {new Date(detail.applicant_account_created_at).toLocaleString()}
            </p>
            {detail.creator_hello_text && (
              <p>
                <span className="opacity-60">Profile hello-text:</span>{" "}
                {detail.creator_hello_text}
              </p>
            )}
            {/* The "View public profile" link was removed — /creators/
                {handle} public pages no longer exist (profiles are
                admin-or-self only). The hello-text above plus the handle
                cover what the reviewer needs here. */}
            {detail.creator_handle && (
              <p>
                <span className="opacity-60">Handle:</span>{" "}
                <code className="font-mono text-xs">@{detail.creator_handle}</code>
              </p>
            )}
            {/* Preview share — what a certificate of this applicant
                would look like if they join the dossier (portrait,
                hello text, signature ink). */}
            <div className="pt-2">
              <p className="opacity-60 mb-2">Preview certificate</p>
              <CreatorPreviewShare
                handle={detail.creator_handle ?? null}
                fallbackName={detail.creator_display_name ?? "Creator"}
              />
            </div>
          </CardContent>
        </Card>

        {/* Right — portfolio context + effective hello-text */}
        <Card>
          <CardHeader>
            <CardTitle>Portfolio context</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>
              <span className="opacity-60">Recruiting status:</span>{" "}
              <code className="text-xs">{detail.portfolio_recruiting_status}</code>
            </p>
            {detail.portfolio_theme && (
              <p>
                <span className="opacity-60">Theme:</span>{" "}
                {detail.portfolio_theme}
              </p>
            )}
            <p>
              <span className="opacity-60">Submitted:</span>{" "}
              {new Date(detail.submitted_at).toLocaleString()}
            </p>
            {detail.reviewed_at && (
              <p>
                <span className="opacity-60">Decided:</span>{" "}
                {new Date(detail.reviewed_at).toLocaleString()}
              </p>
            )}
            <div className="mt-3 pt-3 border-t">
              <p className="opacity-60 mb-1">
                Effective hello-text (lands on share certificates):
              </p>
              <blockquote className="border-l-2 pl-3 italic">
                {detail.effective_hello_text ?? "(none)"}
              </blockquote>
              {detail.optional_drop_specific_hello_text && (
                <p className="text-xs opacity-60 mt-1">
                  Drop-specific override applied.
                </p>
              )}
            </div>
            {detail.rejection_reason && (
              <div className="mt-3 pt-3 border-t">
                <p className="opacity-60 mb-1">Rejection reason:</p>
                <blockquote className="border-l-2 pl-3">
                  {detail.rejection_reason}
                </blockquote>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action bar */}
      {isPending && (
        <div className="sticky bottom-4 bg-brand-paper-100 rounded-lg p-4 shadow flex items-center justify-end gap-3">
          <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
            <Button
              variant="destructive"
              onClick={() => setRejectOpen(true)}
            >
              Reject
            </Button>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reject this application?</AlertDialogTitle>
                <AlertDialogDescription>
                  Optional — leave blank if you&apos;d rather not say.
                  {` `}
                  {REJECT_REASON_MIN}…{REJECT_REASON_MAX} chars if provided.
                  The creator stays approved overall and can re-apply.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="my-3 space-y-1">
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxCharacters={REJECT_REASON_MAX}
                  rows={4}
                  placeholder="Optional reason…"
                  aria-label="Optional rejection reason"
                />
                {reasonInvalid && (
                  <p className="text-xs text-destructive">
                    Reason must be at least {REJECT_REASON_MIN} characters, or
                    leave blank to skip.
                  </p>
                )}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setReason("")}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleReject}
                  disabled={submitDisabled}
                >
                  {reject.isPending ? "Rejecting…" : "Reject"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            variant="primary"
            onClick={handleApprove}
            disabled={approve.isPending}
          >
            {approve.isPending ? "Approving…" : "Approve"}
          </Button>
        </div>
      )}
    </>
  );
}
